import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Button, TextButton, Typography, useOverlayContext } from 'seagull';
import { BankAccountDetailsModal } from "../BankAccountDetailsModal";
import ContactSupportButton from "../components/ContactSupportButton";
import ExternalLinkButton from "../components/ExternalLinkButton";
import { useStore, useStoreActions } from "../contexts";
import { BusinessChannelEventBinder, PusherEvent } from "../pusher";
import { selectors } from "../store/state-selectors";
import { isBankAccountConnected } from "../store/store.model";
import { helpArticles } from "../utils/helpArticles";
import { PlaidMode, usePlaidState } from "./usePlaidState";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const useConnectBankAccount = () => {
  const [plaidCanceled, setPlaidCanceled] = useState(false);
  const [isConnectingInProgress, setIsConnectingInProgress] = useState(false);
  const overlayContext = useOverlayContext();
  const {
    api,
    logBI,
    fetchUserInfo
  } = useStoreActions();
  const plaidLink = usePlaidState({
    mode: {
      type: PlaidMode.Vendor
    },
    onSuccess(_account) {
      logBI(11210);
    },
    onError(_error) {
      logBI(11211);
      setPlaidCanceled(true);
      setIsConnectingInProgress(false);
    },
    onExit() {
      setPlaidCanceled(true);
      setIsConnectingInProgress(false);
    }
  });
  const onVendorBankAccountConnectedPushEvent = async _event => {
    await fetchUserInfo();
    setIsConnectingInProgress(false);
  };
  const ConnectBankAccountEventBinder = () => /*#__PURE__*/_jsx(BusinessChannelEventBinder, {
    source: "mgmt",
    eventName: PusherEvent.VendorBankAccountConnected,
    callback: onVendorBankAccountConnectedPushEvent
  });
  const onDetailsSubmit = async _ref => {
    let {
      routingNumber,
      accountNumber
    } = _ref;
    logBI(11212);
    overlayContext.startLoader();
    try {
      await api.vendor.authorizeVendorBankAccount({
        routingNumber,
        accountNumber
      });
    } catch (err) {
      Sentry.captureException(err);
      overlayContext.showErrorToast('Error submitting bank account details');
      logBI(11213);
    } finally {
      overlayContext.stopLoader();
    }
  };
  const Description = _ref2 => {
    let {
      testId,
      linkTestId,
      className,
      style = {}
    } = _ref2;
    return /*#__PURE__*/_jsxs("div", {
      "data-testid": testId,
      className: className,
      children: [/*#__PURE__*/_jsx(Typography, {
        style: style,
        children: "Add your bank account details to receive payments. "
      }), /*#__PURE__*/_jsxs(Typography, {
        style: style,
        children: ["Note that ACH fees may apply. ", /*#__PURE__*/_jsx(ExternalLinkButton, {
          href: helpArticles.paymentsViaAnchor(),
          testId: linkTestId,
          openInNewTab: true,
          underline: "none",
          children: "Learn more"
        })]
      })]
    });
  };
  const ConnectButton = _ref3 => {
    let {
      testId,
      className,
      enterManuallyTestId
    } = _ref3;
    const userInfo = useStore(state => state.userInfo);
    const hasBankAccountConnected = selectors.user.hasBankAccountConnected({
      userInfo
    });
    if (hasBankAccountConnected) return /*#__PURE__*/_jsx(_Fragment, {});
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ConnectBankAccountEventBinder, {}), /*#__PURE__*/_jsxs("div", {
        className: className,
        children: [/*#__PURE__*/_jsx(Button
        // mui button takes up fullwidth by default when wrapped by a flex-column container, this neutralizes that
        , {
          style: {
            width: 'fit-content'
          },
          size: "medium",
          testId: testId,
          loading: isConnectingInProgress,
          onClick: () => {
            logBI(11204);
            setIsConnectingInProgress(true);
            plaidLink.open();
          },
          children: 'Connect bank'
        }), plaidCanceled && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Typography, {
            testId: "".concat(enterManuallyTestId, "-description"),
            style: {
              marginTop: '12px'
            },
            variant: "paragraph2",
            skin: "secondary",
            children: "Having trouble connecting your account?"
          }), /*#__PURE__*/_jsx(TextButton, {
            testId: "".concat(enterManuallyTestId, "-btn"),
            onClick: () => {
              logBI(11205);
              overlayContext.showModal({
                content: _ref4 => {
                  let {
                    dismissModal
                  } = _ref4;
                  return /*#__PURE__*/_jsx(BankAccountDetailsModal, {
                    onSubmit: onDetailsSubmit,
                    onCancel: dismissModal
                  });
                }
              });
            },
            underline: "none",
            children: "Connect using routing and account number"
          })]
        })]
      })]
    });
  };
  const BankAccountDetails = _ref5 => {
    let {
      payoutBankAccount,
      style = {}
    } = _ref5;
    return isBankAccountConnected(payoutBankAccount) ? /*#__PURE__*/_jsxs("div", {
      style: {
        display: 'flex',
        flexDirection: 'column'
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        testId: "connected-bank-account-details",
        style: style,
        children: "The connected bank account is ****".concat(payoutBankAccount.connected.last4)
      }), /*#__PURE__*/_jsxs(Typography, {
        skin: "secondary",
        style: style,
        children: ["For bank account changes, please ", /*#__PURE__*/_jsx(ContactSupportButton, {
          underline: "none",
          testId: "contact-support"
        })]
      })]
    }) : /*#__PURE__*/_jsx(_Fragment, {});
  };
  return {
    Description,
    ConnectButton,
    BankAccountDetails
  };
};